(() => {
  if (window.MaxlerProteinSticky) return;

  class ProteinGallerySticky {
    constructor(root, {signal} = {}) {
      this.root = root;
      this.signal = signal;
      this.column = root?.querySelector('.protein-gallery-column');
      this.gallery = this.column?.querySelector('.protein-gallery');
      this.layout = root?.querySelector('.protein-layout');
      this.header = document.querySelector('#SiteHeader');
      this.headerWrapper = document.querySelector('#HeaderWrapper');
      this.controller = new AbortController();
      this.abortSignal = this.controller.signal;
      this.disposed = false;
      this.frame = 0;
      this.headerFrame = 0;
      this.headerTransitionUntil = 0;
      this.media = window.matchMedia('(min-width: 990px)');
      this.originalStyles = {
        columnPosition: this.column?.style.getPropertyValue('position') || '',
        columnTop: this.column?.style.getPropertyValue('top') || '',
        galleryPosition: this.gallery?.style.getPropertyValue('position') || '',
        galleryTop: this.gallery?.style.getPropertyValue('top') || '',
        galleryWidth: this.gallery?.style.getPropertyValue('width') || '',
        galleryMaxWidth: this.gallery?.style.getPropertyValue('max-width') || '',
        galleryAlignSelf: this.gallery?.style.getPropertyValue('align-self') || '',
        galleryFlex: this.gallery?.style.getPropertyValue('flex') || ''
      };
      this.originalVars = {
        top: root?.style.getPropertyValue('--protein-gallery-top') || '',
        size: root?.style.getPropertyValue('--protein-gallery-size') || ''
      };

      if (!this.root || !this.column || !this.gallery || !this.layout || signal?.aborted) {
        this.dispose();
        return;
      }

      this.gallery.style.setProperty('position', 'sticky');
      this.addListeners();
      this.observeGeometry();
      signal?.addEventListener('abort', () => this.dispose(), {once: true});
      this.schedule();
    }

    addListeners() {
      const signal = this.abortSignal;
      window.addEventListener('resize', this.onGeometryChange, {passive: true, signal});
      window.addEventListener('scroll', this.onScroll, {passive: true, signal});
      this.media.addEventListener('change', this.onGeometryChange, {signal});
      this.root.addEventListener('variantChange', this.onGeometryChange, {signal});
      this.root.addEventListener('change', this.onGeometryChange, {signal});
      this.header?.addEventListener('transitionrun', this.onHeaderTransition, {signal});
      this.header?.addEventListener('transitionend', this.onHeaderTransitionEnd, {signal});
      this.header?.addEventListener('transitioncancel', this.onHeaderTransitionEnd, {signal});
    }

    observeGeometry() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onGeometryChange);
        [this.root, this.layout, this.column, this.gallery, this.header].filter(Boolean).forEach(element => this.resizeObserver.observe(element));
      }
      if ('MutationObserver' in window) {
        this.mutationObserver = new MutationObserver(records => {
          const headerOnly = records.every(record => record.target === this.header || record.target === this.headerWrapper);
          if (!headerOnly || this.headerFrame === 0) this.schedule();
        });
        this.mutationObserver.observe(this.gallery, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden']});
        [this.header, this.headerWrapper].filter(Boolean).forEach(element => {
          this.mutationObserver.observe(element, {attributes: true, attributeFilter: ['class', 'style']});
        });
      }
    }

    onGeometryChange = () => this.schedule();
    onScroll = () => this.schedule();

    onHeaderTransition = event => {
      if (event.propertyName !== 'transform' || this.headerFrame) return;
      this.headerTransitionUntil = performance.now() + 600;
      const tick = () => {
        this.schedule();
        if (!this.disposed && performance.now() < this.headerTransitionUntil) this.headerFrame = requestAnimationFrame(tick);
        else this.headerFrame = 0;
      };
      this.headerFrame = requestAnimationFrame(tick);
    };

    onHeaderTransitionEnd = event => {
      if (event.propertyName !== 'transform') return;
      this.headerTransitionUntil = 0;
      if (this.headerFrame) cancelAnimationFrame(this.headerFrame);
      this.headerFrame = 0;
      this.schedule();
    };

    schedule() {
      if (this.disposed || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    headerOffset() {
      if (!this.header?.classList.contains('site-header--stuck')) return 16;
      const style = getComputedStyle(this.header);
      const rect = this.header.getBoundingClientRect();
      const visible = style.position === 'fixed' && style.display !== 'none' && style.visibility !== 'hidden' &&
        Number(style.opacity) > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      return visible ? Math.max(16, rect.bottom + 16) : 16;
    }

    setVar(name, value) {
      if (this.root.style.getPropertyValue(name) !== value) this.root.style.setProperty(name, value);
    }

    setStyle(element, name, value) {
      if (element?.style.getPropertyValue(name) !== value) element?.style.setProperty(name, value);
    }

    update() {
      if (!this.column.isConnected || !this.gallery.isConnected || !this.layout.isConnected) return;
      if (!this.media.matches || getComputedStyle(this.layout).display !== 'grid') {
        this.resetResponsiveStyles();
        return;
      }

      const columnWidth = this.column.getBoundingClientRect().width;
      const headerTop = this.headerOffset();
      const hasThumbRail = !this.gallery.classList.contains('protein-gallery--single') &&
        Boolean(this.gallery.querySelector('[data-product-thumbs], .product__thumbs--beside'));
      const railWidth = hasThumbRail ? 92 : 0;
      const squareSize = Math.max(0, Math.min(columnWidth - railWidth, window.innerHeight - headerTop - 32));
      const fullGalleryWidth = squareSize + railWidth;
      const enoughHeight = squareSize >= 160;

      this.setStyle(this.gallery, 'position', enoughHeight ? 'sticky' : 'static');
      this.setStyle(this.gallery, 'top', `${headerTop}px`);
      this.setVar('--protein-gallery-top', `${headerTop}px`);
      this.setVar('--protein-gallery-size', `${fullGalleryWidth}px`);

      if (squareSize > 0) {
        this.setStyle(this.gallery, 'width', `${fullGalleryWidth}px`);
        this.setStyle(this.gallery, 'max-width', '100%');
        this.setStyle(this.gallery, 'align-self', 'start');
        this.setStyle(this.gallery, 'flex', '0 0 auto');
      }

      const size = Math.round(squareSize * 100) / 100;
      if (size !== this.lastSquareSize) {
        this.lastSquareSize = size;
        const slideshow = this.gallery.querySelector('.product-slideshow.flickity-enabled, .flickity-enabled');
        const instance = slideshow && (window.Flickity?.data?.(slideshow) || slideshow.flickityInstance);
        instance?.resize?.();
      }
    }

    resetResponsiveStyles() {
      const restore = (element, name, value) => {
        if (!element) return;
        if (value) this.setStyle(element, name, value);
        else if (element.style.getPropertyValue(name)) element.style.removeProperty(name);
      };
      restore(this.column, 'position', this.originalStyles.columnPosition);
      restore(this.column, 'top', this.originalStyles.columnTop);
      if (this.gallery) {
        restore(this.gallery, 'position', this.originalStyles.galleryPosition);
        restore(this.gallery, 'top', this.originalStyles.galleryTop);
        restore(this.gallery, 'width', this.originalStyles.galleryWidth);
        restore(this.gallery, 'max-width', this.originalStyles.galleryMaxWidth);
        restore(this.gallery, 'align-self', this.originalStyles.galleryAlignSelf);
        restore(this.gallery, 'flex', this.originalStyles.galleryFlex);
      }
      if (this.root) {
        if (this.originalVars.top) this.setVar('--protein-gallery-top', this.originalVars.top);
        else if (this.root.style.getPropertyValue('--protein-gallery-top')) this.root.style.removeProperty('--protein-gallery-top');
        if (this.originalVars.size) this.setVar('--protein-gallery-size', this.originalVars.size);
        else if (this.root.style.getPropertyValue('--protein-gallery-size')) this.root.style.removeProperty('--protein-gallery-size');
      }
    }

    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      this.controller.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.headerFrame) cancelAnimationFrame(this.headerFrame);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.resetResponsiveStyles();
    }
  }

  window.MaxlerProteinSticky = (root, options) => new ProteinGallerySticky(root, options);
})();
